import type { HttpMethod } from '@common/httpMethod';
import type { FetchErrorMessage } from './fetchError';
import { AbortError } from './abortError';
import { FetchError } from './fetchError';
import { TimeoutError } from './timeoutError';
import { getCsrfToken } from './utils/csrfToken';

export interface IFetchResult<Json = any> {
    errorCode?: string;
    json?: Json;
    message?: string;
    status: number;
}

/** @deprecated use fetchJson */
export function fetch(url: string, method: string, body?: unknown, signal?: AbortSignal) {
    let bodyString;
    if (body) {
        bodyString = JSON.stringify(body);
    }

    return window.fetch(url, {
        body: bodyString,
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'csrf-token': getCsrfToken() ?? '',
        },
        method,
        signal,
    });
}

export async function getJson<T = any>(url: string, signal?: AbortSignal) {
    return fetchJson<T>({ method: 'GET', signal, url });
}

export async function deleteJson<T = any>(url: string, signal?: AbortSignal) {
    return fetchJson<T>({ method: 'DELETE', signal, url });
}

export async function postJson<T = any>(url: string, body: object, signal?: AbortSignal) {
    return fetchJson<T>({ body, method: 'POST', signal, url });
}

type FetchParams = {
    allowedErrorCodes?: string[];
    allowedHttpErrorCodes?: number[];
    body?: unknown;
    method: HttpMethod;
    signal?: AbortSignal;
    url: string;
};

export async function fetchJson<T = any>(params: FetchParams): Promise<IFetchResult<T>>;
/**
 * @deprecated use fetchJson(params: FetchParams)
 */
export async function fetchJson<T = any>(
    url: string,
    method: HttpMethod,
    body?: unknown,
    allowedErrorCodes?: string[],
    allowedHttpErrorCodes?: number[],
    signal?: AbortSignal
): Promise<IFetchResult<T>>;
export async function fetchJson<T = any>(
    ...params: [string, HttpMethod, unknown?, string[]?, number[]?, AbortSignal?] | [FetchParams]
): Promise<IFetchResult<T>> {
    if (typeof params[0] === 'string') {
        const [url, method, body, allowedErrorCodes, allowedHttpErrorCodes, signal] = params as [
            string,
            HttpMethod,
            unknown?,
            string[]?,
            number[]?,
            AbortSignal?,
        ];

        return fetchJson({
            allowedErrorCodes,
            allowedHttpErrorCodes,
            body,
            method,
            signal,
            url,
        });
    }

    const { allowedErrorCodes, allowedHttpErrorCodes, body, method, signal, url } = params[0];

    const bodyString = body ? JSON.stringify(body) : undefined;

    if (window.fetch === undefined) {
        throw new Error('window.fetch is undefined');
    }

    const response = await window
        .fetch(url, {
            body: bodyString,
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'csrf-token': getCsrfToken() ?? '',
            },
            method,
            signal,
        })
        .catch((e) => {
            if (!window.closed) {
                throw e;
            }
            throw new AbortError();
        });

    const statusCode = response.status;

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.indexOf('text/html') !== -1) {
        throw new FetchError({
            apiErrorResponse: {
                message: 'Received HTML, does this route exist?',
                statusCode,
            },
            method,
            url,
        });
    }

    if (response.headers.get('Content-Length') && response.headers.get('Content-Length') === '0') {
        return { status: 0 };
    }

    let json;
    if (statusCode !== 404) {
        json = await response.json();
    }

    if (signal?.aborted) {
        throw new AbortError();
    }

    if (statusCode < 400) {
        return { json, status: statusCode };
    }
    const errorCode = json?.code as string;
    const message = json?.message;
    const fetchErrorMessage: FetchErrorMessage = {
        apiErrorResponse: {
            errorCode,
            message,
            statusCode,
        },
        method,
        url,
    };

    if (statusCode >= 400 && statusCode < 500) {
        if (statusCode === 440) {
            throw new TimeoutError('Session has expired');
        }
        if (allowedErrorCodes?.includes(errorCode)) {
            return { errorCode, message, status: statusCode };
        }
        if (allowedHttpErrorCodes?.includes(statusCode)) {
            return { errorCode, status: statusCode };
        }
    }

    if (statusCode === 400) {
        throw new FetchError({
            apiErrorResponse: {
                message: 'Request malformed, not accepted by server',
                statusCode,
            },
            method,
            url,
        });
    }

    throw new FetchError(fetchErrorMessage);
}

export class AbortableFetch {
    private controller = new AbortController();
    private signal: AbortSignal = this.controller.signal;

    public async getJson<T = any>(url: string) {
        return getJson<T>(url, this.signal);
    }

    public async deleteJson<T = any>(url: string) {
        return deleteJson<T>(url, this.signal);
    }

    public async postJson<T = any>(url: string, body: object) {
        return postJson<T>(url, body, this.signal);
    }

    public async fetch(url: string, method: HttpMethod, body?: unknown) {
        return fetch(url, method, body, this.signal);
    }

    public async fetchJson<T = any>(
        url: string,
        method: HttpMethod,
        body?: object,
        allowedErrorCodes?: string[],
        allowedHttpErrorCodes?: number[]
    ) {
        return fetchJson<T>({ allowedErrorCodes, allowedHttpErrorCodes, body, method, signal: this.signal, url });
    }

    public async uploadFile<T = any>(url: string, file: File) {
        return uploadFile<T>(url, file, this.signal);
    }

    public abort() {
        this.controller.abort();
    }
}

export async function uploadFile<T = any>(
    url: string,
    file: File,
    signal?: AbortSignal,
    otherFormData?: FormData
): Promise<IFetchResult<T>> {
    let statusCode = 0;

    const formData = otherFormData ?? new FormData();
    formData.append('file', file);

    const response = await window.fetch(url, {
        body: formData,
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            'csrf-token': getCsrfToken() ?? '',
        },
        method: 'POST',
        signal,
    });

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.indexOf('text/html') !== -1) {
        throw new FetchError({
            apiErrorResponse: {
                message: 'Received HTML, does this route exist?',
                statusCode,
            },
            method: 'POST',
            url,
        });
    }
    if (response.headers.get('Content-Length') && response.headers.get('Content-Length') === '0') {
        return { status: statusCode };
    }
    statusCode = response.status;

    let json;
    if (statusCode !== 404) {
        json = await response.json();
    }

    const errorCode = json?.code as string;
    if (signal?.aborted) {
        throw new AbortError();
    }

    if (statusCode < 400) {
        return { json, status: statusCode };
    }

    if (statusCode >= 400 && statusCode < 500) {
        if (statusCode === 440) {
            throw new TimeoutError('Session has expired');
        }

        if (statusCode === 400) {
            throw new FetchError({
                apiErrorResponse: {
                    message: 'Request malformed, not accepted by server',
                    statusCode,
                },
                method: 'POST',
                url,
            });
        }
    }

    throw new FetchError({
        apiErrorResponse: {
            errorCode,
            message: 'File upload failed',
            statusCode,
        },
        method: 'POST',
        url,
    });
}
